import type { BasePermissionCapability } from "app/based/baseApiModel/basePermissionMenu/BasePermissionCapability";
import { IFetchDataWithFeature } from "context/model/ContextModel";
import { isEmpty } from "lodash";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { AppBasedServiceInjection } from "utils/serviceRepo/ServiceRepo";

interface ICheckCapabilityOptions {
  disableCapability?: boolean;
  exact?: boolean;
}

export class FeatureFormat {
  static findFeature = (path: string) => {
    let cachedFeature = productCache?.getCache(CacheKeyEnum.internalFeature);
    if (cachedFeature?.some((item) => path?.includes(item?.path))) {
      return true;
    } else {
      return false;
    }
  };

  static validateFeature = (key: string) => {
    let cachedCapabilities = productCache?.getCache(CacheKeyEnum.capabilities);
    return cachedCapabilities?.find((item: string) => item?.includes(key));
  };

  static checkCapability = (
    capabilityKeys?: string | Array<string | undefined | false | null>,
    options?: ICheckCapabilityOptions,
  ) => {
    const { disableCapability = false, exact = false } = options || {};

    if (disableCapability) {
      return true;
    }

    const keys = (
      Array.isArray(capabilityKeys) ? capabilityKeys : [capabilityKeys]
    )?.filter((item) => !!item) as string[];

    if (isEmpty(keys)) {
      return false;
    }

    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities,
    ) as Array<BasePermissionCapability | string>;

    return !!cachedCapabilities?.some((item) => {
      const cachedKey = typeof item === "string" ? item : item?.key;

      return keys?.some((key) =>
        exact ? cachedKey === key : cachedKey?.includes(key),
      );
    });
  };

  static fetchFilterList = async <R extends Object, P extends Object>(
    props: IFetchDataWithFeature<R, P>,
  ) => {
    const {
      dataState,
      path,
      savedKey,
      isForceRefresh,
      params,
      isNeedFeature = false,
      needExpired = true,
      onError,
    } = props;
    try {
      if (isNeedFeature && !findFeature(path)) {
        !!savedKey && productCache?.resetCache(savedKey);
        return undefined;
      } else {
        if (!isEmpty(productCache?.getCache(savedKey)) && !isForceRefresh) {
          dataState(productCache?.getCache(savedKey));
          return productCache?.getCache(savedKey);
        } else {
          const repo = AppBasedServiceInjection.getFeatureRepo();
          const res = await repo.getPathWithFeature<P, R>(path, params);
          if (res?.data) {
            dataState(res?.data);
            if (!!savedKey) {
              productCache.saveCache({
                key: savedKey,
                product: res?.data,
                needExpired,
                timeExpired: 10800,
              });
            }
          }
          return res?.data;
        }
      }
    } catch (error) {
      console.error(error);
      !!onError && onError(error);
    }
  };
}

export const { findFeature, fetchFilterList, checkCapability } = FeatureFormat;
